// ENG-2277 (HTML Forms): print rules that apply only to HTML carrying their markers. Both PDF table
// renderers — the standard react-pdf-html path (`adjustTables`) and Enhanced Table Rendering
// (`PdfDocumentTable`) — read table lines from here, so a table prints the same lines on either path.
import formTextMark from "./form-text-mark.json";

export type TableLinesMode = "all" | "outside" | "rows" | "none";

export const TABLE_LINES_MODES: readonly TableLinesMode[] = [
  "all",
  "outside",
  "rows",
  "none"
];

// The table attribute that opts a table into the per-table line setting. Tables without it keep the
// legacy rule (a grey line on every cell without its own border-style, or #333 under Enhanced Table
// Rendering), so templates created before HTML Forms print exactly as they always did.
export const TABLE_LINES_ATTRIBUTE = "data-lines";

export const TABLE_LINE_WIDTH = 1;
export const TABLE_LINE_STYLE = "solid";
export const TABLE_LINE_COLOR = "#333333";

export type CellSides = {
  top: boolean;
  right: boolean;
  bottom: boolean;
  left: boolean;
};

export type CellPosition = {
  row: number;
  col: number;
  rowSpan?: number;
  colSpan?: number;
  rowCount: number;
  colCount: number;
};

export const readTableLinesMode = (
  value: string | null | undefined
): TableLinesMode | null =>
  TABLE_LINES_MODES.includes(value as TableLinesMode)
    ? (value as TableLinesMode)
    : null;

const NO_SIDES: CellSides = {
  top: false,
  right: false,
  bottom: false,
  left: false
};

/**
 * Which sides of a cell draw a line. A line is never drawn twice: every cell draws its right and bottom
 * edges, and only the first row and first column draw the top and left edges. Enhanced Table Rendering
 * already uses this pattern for its default grid, so both renderers produce the same 1px lines.
 */
export const getCellBorders = (
  mode: TableLinesMode,
  position: CellPosition
): CellSides => {
  const { row, col, rowSpan = 1, colSpan = 1, rowCount, colCount } = position;
  const firstRow = row === 0;
  const firstCol = col === 0;
  const lastRow = row + rowSpan >= rowCount;
  const lastCol = col + colSpan >= colCount;

  switch (mode) {
    case "all":
      return { top: firstRow, right: true, bottom: true, left: firstCol };
    case "outside":
      return { top: firstRow, right: lastCol, bottom: lastRow, left: firstCol };
    case "rows":
      return { ...NO_SIDES, bottom: !lastRow };
    case "none":
    default:
      return NO_SIDES;
  }
};

/**
 * CSS declarations for a cell's lines on the react-pdf-html path. Each side is written explicitly,
 * including the sides without a line, so nothing inherited or defaulted can draw one.
 */
export const cellBorderDeclarations = (sides: CellSides): string[] =>
  (["top", "right", "bottom", "left"] as const).map((side) =>
    sides[side]
      ? `border-${side}: ${TABLE_LINE_WIDTH}px ${TABLE_LINE_STYLE} ${TABLE_LINE_COLOR}`
      : `border-${side}-width: 0px`
  );

// --- Signature and logo boxes -------------------------------------------------------------------

// A fixed-size box (a signature line, a logo slot). Its content shrinks to fit and the box never
// grows, so nothing around it moves between unsigned and signed.
export const BOX_ATTRIBUTE = "data-box";
// "false" drops the line under the box; anything else (including absent) keeps it.
export const BOX_LINE_ATTRIBUTE = "data-box-line";
export const BOX_LINE_STYLE = "1px solid #000000";

// --- Blank fields (SN-19949) ------------------------------------------------------------------

// In the print dialog each run of 3+ underscores becomes a field staff type into: a non-editable shell holding an
// editable text island. The editor writes these; printBlankFields turns them back into text on a line for the PDF.
export const BLANK_FIELD_ATTRIBUTE = "data-blank-field";
export const BLANK_TEXT_ATTRIBUTE = "data-blank-text";
// The blank's original underscore count: an empty field prints exactly that.
export const BLANK_LENGTH_ATTRIBUTE = "data-blank-length";
// How many underscores fill the rest of the line after the typed text, kept up to date as it is typed.
export const BLANK_PAD_ATTRIBUTE = "data-blank-pad";
// Set on a blank whose label names a date; it gets a date picker.
export const BLANK_DATE_ATTRIBUTE = "data-blank-date";

// Lengths are normalised to inches and react-pdf converts them with the page's own dpi, so a box
// keeps its physical size on any page (Forms print at FORM_PAGE_DPI, 96; legacy templates at dpi 128). CSS px
// are 96 per inch, as in the editor.
const INCHES_PER_UNIT: Record<string, number> = {
  in: 1,
  pt: 1 / 72,
  px: 1 / 96,
  cm: 1 / 2.54,
  mm: 1 / 25.4
};

/** A CSS length in inches, or null for anything that is not an absolute length (auto, %, em...). */
export const cssLengthToInches = (
  value: string | undefined | null
): number | null => {
  const match = /^\s*([\d.]+)\s*(in|pt|px|cm|mm)\s*$/i.exec(value ?? "");
  if (!match) return null;
  const inches = parseFloat(match[1]) * INCHES_PER_UNIT[match[2].toLowerCase()];
  return Number.isFinite(inches) && inches > 0
    ? Math.round(inches * 1000) / 1000
    : null;
};

const styleValue = (style: string, property: string): string | undefined =>
  style
    .split(";")
    .map((declaration) => declaration.split(":"))
    .find(([name]) => name?.trim().toLowerCase() === property)?.[1]
    ?.trim();

/** The box's width and height in inches, from its inline style; null when either is unusable. */
export const readBoxSize = (
  style: string | undefined | null
): { width: number; height: number } | null => {
  const width = cssLengthToInches(styleValue(style ?? "", "width"));
  const height = cssLengthToInches(styleValue(style ?? "", "height"));
  return width && height ? { width, height } : null;
};

// --- Form page settings ---------------------------------------------------------------------------

export type FormPageSize = "letter" | "a4";

export type FormMargins = {
  top: number;
  right: number;
  bottom: number;
  left: number;
};

/** A Form's print settings (TextTemplate.form_settings). Templates without them are not Forms. */
/**
 * Whether the print dialog opens a Digital Form with Suno's clinic header and footer signature lines
 * on. Named as the practice's form_rendering_defaults; a missing one is off, because an uploaded form
 * usually carries the practice's own header and signature lines.
 */
export type FormPrintDefaults = {
  clinic_header?: boolean;
  provider_signature_in_footer?: boolean;
  patient_signature_in_footer?: boolean;
  /** Shrink the filled-in form onto one page when it prints (a purchase agreement's invoice rows). */
  fit_one_page?: boolean;
};

export type FormSettings = {
  version: 1;
  page_size: FormPageSize;
  margins_in: FormMargins;
  default_table_lines: TableLinesMode;
  print_defaults?: FormPrintDefaults;
  /** The margins the form was converted with, kept so "As converted" can bring them back. */
  converted_margins_in?: FormMargins;
};

// A Form prints at the CSS reference resolution, 96 px per inch, so a length means the same in the
// PDF as in the editor and a line usually breaks at the same word in both. Not always: react-pdf lets
// a space shrink by up to a third to fit one more word on a line, and a browser never does
// (tests/features/settings/eng-2277-editor-margins.spec.ts keeps a sensor on it). react-pdf scales px and pt alike
// by 1/dpi, so the normalizer rewrites a Form's pt lengths as px first. Other templates keep their
// dpi of 128 and print exactly as before.
export const FORM_PAGE_DPI = 96;

// The dpi every other document's page prints at (PdfDocument's default).
export const LEGACY_PAGE_DPI = 128;

// Forms are set at single line spacing, as paper forms are (1.15 to 1.2 on the forms practices sent
// us). The legacy 1.4 made a dense one-page form print on two. The editor and the PDF both use it.
export const FORM_LINE_HEIGHT = 1.2;

const PAGE_WIDTH_IN: Record<FormPageSize, number> = {
  letter: 8.5,
  a4: 595.28 / 72
};

/**
 * Marks a Form page's text for the line breaking a browser does (postinstall.js,
 * patchReactPdfFormLineBreaking): whole words at their natural width, so a line breaks at the same word
 * in the PDF as in the editor. The number is in form-text-mark.json, which postinstall.js reads too. It
 * prints as fully opaque (pdfkit clamps opacity to 1), and it is not 1 itself, because other documents'
 * text does carry `opacity: 1` and must keep the line breaking it has.
 *
 * One thing does print differently: react-pdf draws text at its opacity in place of its colour's own
 * alpha, so text in a see-through colour (`rgba(0, 0, 0, 0.5)`) prints solid on a Form.
 */
export const FORM_TEXT_MARK = { opacity: formTextMark.opacity } as const;

export const formPageLayout = ({ page_size, margins_in }: FormSettings) => ({
  size: page_size === "a4" ? ("A4" as const) : ("LETTER" as const),
  dpi: FORM_PAGE_DPI,
  padding: {
    paddingTop: `${margins_in.top}in`,
    paddingRight: `${margins_in.right}in`,
    paddingBottom: `${margins_in.bottom}in`,
    paddingLeft: `${margins_in.left}in`
  }
});

/** The Form's page width, in CSS px. */
export const formPageWidthPx = ({ page_size }: FormSettings): number =>
  Math.round(PAGE_WIDTH_IN[page_size] * FORM_PAGE_DPI);

/** The width between the Form's left and right margins, in CSS px. */
export const formContentWidthPx = ({
  page_size,
  margins_in
}: FormSettings): number =>
  Math.floor(
    (PAGE_WIDTH_IN[page_size] - margins_in.left - margins_in.right) *
      FORM_PAGE_DPI
  );

// "12pt" -> "16px". Lookbehind keeps it to whole numbers, so "x2pt" or a class-like token is left.
const POINT_LENGTH = /(?<![\w.-])(\d*\.?\d+)pt\b/gi;

/** An inline style with every pt length rewritten as px, for Form mode. */
export const pointLengthsToPixels = (style: string): string =>
  style.replace(
    POINT_LENGTH,
    (_, value: string) =>
      `${Math.round(parseFloat(value) * (4 / 3) * 100) / 100}px`
  );

// A Form's cell without its own padding gets this, in the PDF and in the Form editor alike.
export const FORM_CELL_PADDING_PX = { vertical: 2, horizontal: 4 } as const;

// Checkbox glyphs a Form prints as text, so they stay inline ("☐ Left ☐ Right"): react-pdf-html lays
// an <img> out as a block. The fonts Lato and Courier have no ballot box, but Liberation Sans has
// these two squares.
export const CHECKBOX_PRINT_GLYPHS: Readonly<Record<string, string>> = {
  "☐": "□",
  "☑": "■",
  "☒": "■"
};

// Dingbats (ZapfDingbats, Wingdings) reach a converted Form as text, and none of a Form's fonts has
// them, so they print as the nearest glyph Liberation Sans has. Found on a privacy notice (➤).
export const SYMBOL_PRINT_GLYPHS: Readonly<Record<string, string>> = {
  "➤": "►",
  "➢": "►",
  "➔": "►",
  "▶": "►",
  "✓": "√",
  "✔": "√",
  "✗": "×",
  "✘": "×",
  "✕": "×",
  "✖": "×",
  "❍": "○",
  "❑": "□",
  "❏": "□",
  "❒": "□",
  "◆": "♦",
  "❖": "♦"
};
